import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, User, Mail, Phone, MapPin, Calendar, Car, CreditCard,
  Star, Headphones, Edit2, Ban, CheckCircle, Clock, AlertTriangle,
  DollarSign, Package, FileText, History
} from 'lucide-react';

const STATUS_COLORS = {
  ATIVO: 'bg-green-100 text-green-700',
  INATIVO: 'bg-gray-100 text-gray-700',
  SUSPENSO: 'bg-yellow-100 text-yellow-700',
  BLOQUEADO: 'bg-red-100 text-red-700',
  ENCERRADO: 'bg-slate-100 text-slate-700',
  active: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  cancelled: 'bg-red-100 text-red-700',
  paid: 'bg-green-100 text-green-700',
  PENDING: 'bg-yellow-100 text-yellow-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700'
};

export default function AdminClientDetail() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusModal, setStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [observation, setObservation] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchClientDetail();
  }, [user, navigate, clientId]);

  const fetchClientDetail = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/dashboard-v2/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar dados do cliente');
      navigate('/control-portal-clube/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/update-user-status`, {
        user_id: clientId,
        status: newStatus,
        observation: observation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status atualizado com sucesso!');
      setStatusModal(false);
      fetchClientDetail();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <AdminLayout title="Carregando..." subtitle="Detalhes do cliente">
        <div className="space-y-6">
          <Skeleton className="h-48 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  const client = data?.client || {};
  const vehicles = data?.vehicles || [];
  const subscriptions = data?.subscriptions || [];
  const appointments = data?.appointments || [];
  const payments = data?.payments || [];
  const reviews = data?.reviews || [];
  const tickets = data?.tickets || [];
  const stats = data?.stats || {};

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/control-portal-clube/clients')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Detalhes do Cliente</h1>
            <p className="text-slate-500">Informações completas e histórico</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex items-start gap-4">
              <Avatar className="w-20 h-20">
                <AvatarFallback className="bg-blue-100 text-blue-700 text-2xl">
                  {getInitials(client.name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-xl font-bold">{client.name}</h2>
                <p className="text-slate-500">{client.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={STATUS_COLORS[client.status] || STATUS_COLORS.ATIVO}>
                    {client.status || 'ATIVO'}
                  </Badge>
                  {client.email_verified ? (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" /> E-mail verificado
                    </Badge>
                  ) : (
                    <Badge className="bg-yellow-100 text-yellow-700">
                      <AlertTriangle className="w-3 h-3 mr-1" /> E-mail pendente
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">CPF/CNPJ</p>
                  <p className="text-sm font-medium">{client.cpf_cnpj || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Cadastro</p>
                  <p className="text-sm font-medium">{formatDate(client.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Total Gasto</p>
                  <p className="text-sm font-medium">{formatCurrency(stats.total_spent)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Agendamentos</p>
                  <p className="text-sm font-medium">{stats.total_appointments || 0}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => {
                setNewStatus(client.status || 'ATIVO');
                setObservation(client.status_observation || '');
                setStatusModal(true);
              }}>
                <Edit2 className="w-4 h-4 mr-2" /> Alterar Status
              </Button>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="vehicles" className="space-y-4">
          <TabsList className="grid grid-cols-6 w-full max-w-2xl">
            <TabsTrigger value="vehicles">Veículos</TabsTrigger>
            <TabsTrigger value="subscriptions">Assinaturas</TabsTrigger>
            <TabsTrigger value="appointments">Agendamentos</TabsTrigger>
            <TabsTrigger value="payments">Pagamentos</TabsTrigger>
            <TabsTrigger value="reviews">Avaliações</TabsTrigger>
            <TabsTrigger value="support">Suporte</TabsTrigger>
          </TabsList>

          {/* Veículos */}
          <TabsContent value="vehicles">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Car className="w-5 h-5" /> Veículos Cadastrados
              </h3>
              {vehicles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {vehicles.map((vehicle, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Car className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">{vehicle.brand} {vehicle.model}</p>
                          <p className="text-sm text-slate-500">Placa: {vehicle.plate || 'N/A'}</p>
                          <Badge className="mt-1">{vehicle.vehicle_type || 'N/A'}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Car className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum veículo cadastrado</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Assinaturas */}
          <TabsContent value="subscriptions">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" /> Assinaturas
              </h3>
              {subscriptions.length > 0 ? (
                <div className="space-y-3">
                  {subscriptions.map((sub, idx) => (
                    <div key={idx} className="p-4 border rounded-lg flex items-center justify-between">
                      <div>
                        <p className="font-medium">{sub.plan || 'Plano'}</p>
                        <p className="text-sm text-slate-500">
                          {formatDate(sub.start_date)} - {formatDate(sub.end_date)}
                        </p>
                      </div>
                      <Badge className={STATUS_COLORS[sub.status] || 'bg-gray-100 text-gray-700'}>
                        {sub.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma assinatura</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Agendamentos */}
          <TabsContent value="appointments">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" /> Histórico de Agendamentos
              </h3>
              {appointments.length > 0 ? (
                <div className="space-y-3">
                  {appointments.map((appt, idx) => (
                    <div key={idx} className="p-4 border rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Calendar className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">{appt.provider_name || 'Prestador'}</p>
                          <p className="text-sm text-slate-500">
                            {appt.date} às {appt.time} • {appt.service_type || 'Serviço'}
                          </p>
                        </div>
                      </div>
                      <Badge className={STATUS_COLORS[appt.status] || 'bg-gray-100 text-gray-700'}>
                        {appt.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum agendamento</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Pagamentos */}
          <TabsContent value="payments">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5" /> Histórico de Pagamentos
              </h3>
              {payments.length > 0 ? (
                <div className="space-y-3">
                  {payments.map((payment, idx) => (
                    <div key={idx} className="p-4 border rounded-lg flex items-center justify-between">
                      <div>
                        <p className="font-medium">{formatCurrency(payment.amount)}</p>
                        <p className="text-sm text-slate-500">
                          {formatDate(payment.created_at)} • {payment.payment_method || 'N/A'}
                        </p>
                      </div>
                      <Badge className={STATUS_COLORS[payment.status] || 'bg-gray-100 text-gray-700'}>
                        {payment.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum pagamento</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Avaliações */}
          <TabsContent value="reviews">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Star className="w-5 h-5" /> Avaliações Feitas
              </h3>
              {reviews.length > 0 ? (
                <div className="space-y-3">
                  {reviews.map((review, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`w-4 h-4 ${i < review.rating ? 'text-amber-500 fill-amber-500' : 'text-gray-200'}`}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-slate-400">{formatDate(review.created_at)}</span>
                      </div>
                      <p className="text-sm">{review.comment || 'Sem comentário'}</p>
                      <p className="text-xs text-slate-500 mt-1">Para: {review.provider_name || 'Prestador'}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Star className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma avaliação feita</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Suporte */}
          <TabsContent value="support">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Headphones className="w-5 h-5" /> Tickets de Suporte
              </h3>
              {tickets.length > 0 ? (
                <div className="space-y-3">
                  {tickets.map((ticket, idx) => (
                    <div key={idx} className="p-4 border rounded-lg flex items-center justify-between">
                      <div>
                        <p className="font-medium">{ticket.subject || 'Ticket #' + ticket.id?.slice(0,8)}</p>
                        <p className="text-sm text-slate-500">{formatDate(ticket.created_at)}</p>
                      </div>
                      <Badge className={STATUS_COLORS[ticket.status] || 'bg-gray-100 text-gray-700'}>
                        {ticket.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Headphones className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum ticket de suporte</p>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de Status */}
      <Dialog open={statusModal} onOpenChange={setStatusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar Status do Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Novo Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ATIVO">ATIVO - Pode usar normalmente</SelectItem>
                  <SelectItem value="INATIVO">INATIVO - Não está usando</SelectItem>
                  <SelectItem value="SUSPENSO">SUSPENSO - Acesso temporariamente bloqueado</SelectItem>
                  <SelectItem value="BLOQUEADO">BLOQUEADO - Acesso permanente bloqueado</SelectItem>
                  <SelectItem value="ENCERRADO">ENCERRADO - Conta encerrada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Observação (opcional)</Label>
              <Textarea
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
                placeholder="Motivo da alteração..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusModal(false)}>Cancelar</Button>
            <Button onClick={handleUpdateStatus} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
